import { useCallback, useEffect, useMemo, useState } from "react"
import {
  createFileRoute,
  redirect,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Building2, Download, ListTree, MapPin, Table2, X } from "lucide-react"
import { toast } from "sonner"
import { resolveEventId, useActiveEvent } from "@/core/active-event"
import {
  CollectionView,
  type CollectionConfig,
} from "@/components/collection/CollectionView"
import type { ColumnSpec } from "@/lib/column-layout"
import {
  parseRouteFocus,
  RoutedCollectionLayout,
  useRouteRecordSelection,
} from "@/lib/routed-collection-layout"
import { downloadMappedCsv } from "@/lib/csv"
import {
  agendaItemLocationsQueryOptions,
  buildFlatTree,
  createLocation,
  locationsKey,
  locationsQueryOptions,
  locationDetailQueryOptions,
  patchLocationInCache,
  type LocationRow,
} from "@/core/locations"
import { LocationRecord } from "@/components/locations/LocationRecord"
import { LocationTree } from "@/components/locations/LocationTree"
import {
  LocationFormFields,
  SubmitFooter,
  emptyLocationForm,
  normalizeLocationForm,
  type NormalizedLocationFormValues,
  type LocationFormValues,
} from "@/components/locations/LocationForm"

type LocationViewMode = "table" | "tree"

const LOCATION_DISPLAY_MODES = [
  { id: "table", label: "Table", icon: <Table2 className="h-3 w-3" /> },
  { id: "tree", label: "Tree", icon: <ListTree className="h-3 w-3" /> },
]

// The Locations LAYOUT route. Route DEPTH = Main columns (mirrors vendors.tsx):
//   /locations               → col 1 (table)
//   /locations/$locationId   → col 1 + 2 (+ location record)
// `$locationId` lives on a MARKER child route; ColumnLayout decides how many fit
// side-by-side. `?focus=2` fullscreens col 2. Legacy `?locationId=…` redirects
// into the path.
export const Route = createFileRoute("/$orgSlug/$eventSlug/locations")({
  validateSearch: (
    s: Record<string, unknown>,
  ): { focus?: number; locationId?: string } => {
    return {
      focus: parseRouteFocus(s.focus),
      // Legacy — accepted only so beforeLoad can redirect it into the path.
      locationId: typeof s.locationId === "string" ? s.locationId : undefined,
    }
  },
  beforeLoad: ({ params, search }) => {
    if (search.locationId) {
      throw redirect({
        to: "/$orgSlug/$eventSlug/locations/$locationId",
        params: { ...params, locationId: search.locationId },
        search: { focus: search.focus },
      })
    }
  },
  loader: ({ context, params }) => {
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      void context.queryClient
        .ensureQueryData(locationsQueryOptions(eventId))
        .catch(() => undefined)
    })
  },
  component: LocationsView,
})

function LocationsView() {
  const params = useParams({ from: "/$orgSlug/$eventSlug/locations" })
  const { focus } = Route.useSearch()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { eventId } = useActiveEvent()
  const [creating, setCreating] = useState(false)
  // Table (flat, sortable, filterable) vs Tree (the real venue → room nesting).
  const [view, setView] = useState<LocationViewMode>("table")

  const { locationId: selectedId } = useRouteRecordSelection("locationId")

  const locsQ = useQuery({ ...locationsQueryOptions(eventId ?? ""), enabled: !!eventId })
  const itemsQ = useQuery({
    ...agendaItemLocationsQueryOptions(eventId ?? ""),
    enabled: !!eventId,
  })

  const locs = useMemo(() => locsQ.data ?? [], [locsQ.data])

  const openLocation = useCallback(
    (id: string) =>
      void navigate({
        to: "/$orgSlug/$eventSlug/locations/$locationId",
        params: { ...params, locationId: id },
        search: {},
      }),
    [navigate, params],
  )
  const openLocationFull = useCallback(
    (id: string) =>
      void navigate({
        to: "/$orgSlug/$eventSlug/locations/$locationId",
        params: { ...params, locationId: id },
        search: { focus: 2 },
      }),
    [navigate, params],
  )
  const closePane = useCallback(
    () => void navigate({ to: "/$orgSlug/$eventSlug/locations", params, search: {} }),
    [navigate, params],
  )

  // name lookup → resolve parentLocationId to a parent name for the Parent column.
  const nameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const l of locs) m.set(l.id, l.name)
    return m
  }, [locs])

  const childCountById = useMemo(() => {
    const m = new Map<string, number>()
    for (const l of locs) {
      if (l.parentLocationId != null) m.set(l.parentLocationId, (m.get(l.parentLocationId) ?? 0) + 1)
    }
    return m
  }, [locs])

  const itemCountById = useMemo(() => {
    const m = new Map<string, number>()
    for (const it of itemsQ.data ?? []) {
      if (it.locationId != null) m.set(it.locationId, (m.get(it.locationId) ?? 0) + 1)
    }
    return m
  }, [itemsQ.data])

  // Selection now lives in the URL; if it points at a row that's gone (delete,
  // realtime), drop back to the bare list.
  useEffect(() => {
    if (selectedId && locs.length > 0 && !locs.some((l) => l.id === selectedId)) {
      closePane()
    }
  }, [selectedId, locs, closePane])

  const error = locsQ.isError
    ? ((locsQ.error as Error)?.message ?? "Failed to load locations")
    : null

  const parentName = useCallback(
    (l: LocationRow): string =>
      l.parentLocationId != null ? (nameById.get(l.parentLocationId) ?? "") : "",
    [nameById],
  )
  const addressLine = useCallback(
    (l: LocationRow): string =>
      [l.addressLocality, l.addressRegion].filter(Boolean).join(", "),
    [],
  )

  const config = useMemo<CollectionConfig<LocationRow>>(() => {
    const exportCsv = (rows: LocationRow[]) => {
      downloadMappedCsv(
        "locations.csv",
        ["Location", "Parent", "City", "Region", "Sub-locations", "Agenda items"],
        rows,
        (l) => [
          l.name,
          parentName(l),
          l.addressLocality ?? "",
          l.addressRegion ?? "",
          childCountById.get(l.id) ?? 0,
          itemCountById.get(l.id) ?? 0,
        ],
      )
    }

    return {
      title: "Locations",
      getId: (l) => l.id,
      searchText: (l) =>
        `${l.name} ${parentName(l)} ${addressLine(l)} ${l.notes ?? ""}`,
      onOpen: (l) => openLocation(l.id),
      onPrefetch: (l) => {
        void qc.prefetchQuery(locationDetailQueryOptions(l.id)).catch(() => undefined)
      },
      getRecordHref: (l) => `/${params.orgSlug}/${params.eventSlug}/locations/${l.id}`,
      onOpenFullscreen: (l) => openLocationFull(l.id),
      onNew: () => setCreating(true),
      newLabel: "Add location",
      defaultSortId: "name",
      defaultSortDir: "asc",
      emptyTitle: "No locations yet",
      emptyBlurb:
        "Locations are reusable venues your agenda items reference. Add the hotel first, then nest meeting rooms, pools, and stages inside it.",
      emptyIcon: <MapPin className="h-5 w-5" />,
      savedViews: [
        { id: "all", label: "All", filter: () => true },
        { id: "top", label: "Top-level", filter: (l) => l.parentLocationId == null },
        { id: "nested", label: "Nested", filter: (l) => l.parentLocationId != null },
      ],
      // Default to grouping by parent so the flat table reads as a nested view —
      // each venue's sub-locations cluster under it; top-level venues sit first.
      groupOptions: [
        {
          id: "parent",
          label: "Parent",
          groupOf: (l) =>
            l.parentLocationId != null ? parentName(l) || "Other" : "Top-level",
          order: (label) => (label === "Top-level" ? 0 : 1),
        },
      ],
      bulkActions: [
        {
          id: "export",
          label: "Export",
          icon: <Download className="h-4 w-4" />,
          run: exportCsv,
        },
      ],
      columns: [
        {
          id: "name",
          header: "Name",
          sortable: true,
          sortValue: (l) => l.name.toLowerCase(),
          grow: true,
          minWidth: 180,
          truncate: true,
          render: (l) => {
            const addr = addressLine(l)
            return (
              <div className="flex items-center gap-2.5">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-plum-100/80 text-plum-700/80 dark:bg-white/[0.06] dark:text-plum-200/70">
                  <Building2 className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <div className="truncate font-semibold text-plum-900 dark:text-plum-50">
                    {l.name || "Untitled location"}
                  </div>
                  {addr && (
                    <div className="truncate text-[11.5px] text-plum-700/55 dark:text-plum-200/45">
                      {addr}
                    </div>
                  )}
                </div>
              </div>
            )
          },
        },
        {
          id: "parent",
          header: "Parent",
          className: "text-plum-700/70 dark:text-plum-200/55",
          sortable: true,
          sortValue: (l) => parentName(l).toLowerCase(),
          minWidth: 120,
          truncate: true,
          render: (l) => {
            const p = parentName(l)
            return p ? (
              p
            ) : (
              <span className="text-plum-700/40 dark:text-plum-200/30">Top-level</span>
            )
          },
        },
        {
          id: "items",
          header: "Agenda items",
          align: "right",
          minWidth: 110,
          sortable: true,
          sortValue: (l) => itemCountById.get(l.id) ?? 0,
          render: (l) => {
            const n = itemCountById.get(l.id) ?? 0
            return n > 0 ? (
              <span className="font-semibold tabular-nums text-plum-900 dark:text-plum-100">
                {n}
              </span>
            ) : (
              <span className="text-plum-700/35 dark:text-plum-200/30">—</span>
            )
          },
        },
        {
          id: "children",
          header: "Sub-locations",
          align: "right",
          minWidth: 110,
          sortable: true,
          sortValue: (l) => childCountById.get(l.id) ?? 0,
          render: (l) => {
            const n = childCountById.get(l.id) ?? 0
            return n > 0 ? (
              <span className="tabular-nums text-plum-700/75 dark:text-plum-200/60">{n}</span>
            ) : (
              <span className="text-plum-700/35 dark:text-plum-200/30">—</span>
            )
          },
        },
      ],
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    openLocation,
    openLocationFull,
    params,
    qc,
    parentName,
    addressLine,
    childCountById,
    itemCountById,
  ])

  const columns = useMemo<ColumnSpec[]>(() => {
    const cols: ColumnSpec[] = [
      {
        id: "list",
        minWidth: 320,
        width: 420,
        node: (
          <CollectionView
            config={config}
            rows={locs}
            activeId={selectedId}
            loading={locsQ.isLoading && locs.length === 0}
            error={error}
            displayModes={LOCATION_DISPLAY_MODES}
            displayMode={view}
            onDisplayMode={(m) => setView(m as LocationViewMode)}
            renderDisplay={(m) =>
              m === "tree" ? (
                <LocationTree
                  rows={locs}
                  activeId={selectedId}
                  onOpen={openLocation}
                  onPrefetch={(id) => {
                    void qc.prefetchQuery(locationDetailQueryOptions(id)).catch(() => undefined)
                  }}
                  itemCountById={itemCountById}
                />
              ) : null
            }
          />
        ),
      },
    ]
    if (selectedId && eventId) {
      cols.push({
        id: "record",
        minWidth: 520,
        fill: true,
        node: (
          <LocationRecord
            locationId={selectedId}
            eventId={eventId}
            allLocations={locs}
            onClose={closePane}
            onDeleted={closePane}
          />
        ),
      })
    }
    return cols
  }, [
    config,
    locs,
    selectedId,
    eventId,
    locsQ.isLoading,
    error,
    closePane,
    view,
    openLocation,
    itemCountById,
    qc,
  ])

  return (
    <RoutedCollectionLayout columns={columns} focus={focus} className="relative h-full min-w-0">
      {/* Contained create form — overlays the whole view container. */}
      {creating && eventId && (
        <CreateLocationOverlay
          eventId={eventId}
          existing={locs}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false)
            openLocation(id)
          }}
        />
      )}
    </RoutedCollectionLayout>
  )
}

// ── Contained create form (overlays the view container, not a body portal) ────
function CreateLocationOverlay({
  eventId,
  existing,
  onClose,
  onCreated,
}: {
  eventId: string
  existing: LocationRow[]
  onClose: () => void
  onCreated: (id: string) => void
}) {
  const qc = useQueryClient()
  const [draft, setDraft] = useState<LocationFormValues>(() => emptyLocationForm())

  const parentOptions = useMemo(
    () => buildFlatTree(existing).map((n) => ({ id: n.loc.id, name: n.loc.name, depth: n.depth })),
    [existing],
  )

  const create = useMutation({
    mutationFn: (values: NormalizedLocationFormValues) =>
      createLocation({ eventId, ...values }),
    onSuccess: (res) => {
      patchLocationInCache(qc, eventId, res.location)
      void qc.invalidateQueries({ queryKey: locationsKey(eventId) })
      toast.success(`Added “${res.location.name}”`)
      onCreated(res.location.id)
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Create failed"),
  })

  const valid = draft.name.trim().length > 0
  const submit = () => {
    if (!valid || create.isPending) return
    create.mutate(normalizeLocationForm(draft))
  }

  return (
    <div className="absolute inset-0 z-40 flex items-start justify-center p-4 pt-[8vh]">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-plum-950/40 backdrop-blur-sm dark:bg-black/50"
      />
      <div className="relative z-10 flex max-h-full w-[min(94%,30rem)] flex-col overflow-hidden rounded-2xl bg-white shadow-fluent-lg ring-1 ring-plum-200/50 dark:bg-surface-raised dark:ring-white/10">
        <div className="flex items-center gap-2 border-b border-plum-200/50 px-5 py-3.5 dark:border-white/10">
          <span className="text-[var(--accent)] dark:text-plum-200/70">
            <MapPin className="h-[18px] w-[18px]" />
          </span>
          <h2 className="text-[15px] font-semibold text-plum-900 dark:text-plum-50">New location</h2>
          <button
            onClick={onClose}
            className="ml-auto grid h-7 w-7 place-items-center rounded text-plum-700/55 transition hover:bg-plum-100/60 dark:text-plum-200/45 dark:hover:bg-white/5"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto scroll-thin px-5 py-4">
          <LocationFormFields
            value={draft}
            onChange={setDraft}
            parentOptions={parentOptions}
            autoFocus
            onSubmitShortcut={submit}
          />
        </div>

        <div className="border-t border-plum-200/50 px-5 py-3.5 dark:border-white/10">
          <SubmitFooter
            onCancel={onClose}
            onSubmit={submit}
            submitLabel={create.isPending ? "Creating…" : "Create"}
            busy={create.isPending}
            disabled={!valid}
          />
        </div>
      </div>
    </div>
  )
}
